import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Bug,
  Copy,
  Loader2,
  MessageSquare,
  Paperclip,
  Pencil,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { formatDateTime } from "@/lib/utils";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { FileUploadZone } from "@/modules/media/components/FileUploadZone";
import {
  useIssue,
  useDeleteIssue,
  useCreateComment,
  useEditComment,
  useDeleteComment,
} from "../hooks/useIssues";
import { NewIssueDrawer } from "../components/NewIssueDrawer";
import { IssueStatusBadge, PriorityBadge } from "../components/badges";
import type { IssueComment } from "../types";

function humanize(v: string | null): string {
  if (!v) return "—";
  return v.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function InfoField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-sm text-foreground">{value}</div>
    </div>
  );
}

function CommentItem({
  comment,
  issueId,
  isReply = false,
}: {
  comment: IssueComment;
  issueId: string;
  isReply?: boolean;
}) {
  const { user } = useAuth();
  const isOwn = user?.id === comment.authorId;

  const editComment = useEditComment(issueId);
  const deleteComment = useDeleteComment(issueId);
  const createComment = useCreateComment(issueId);

  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(comment.content);
  const [replying, setReplying] = useState(false);
  const [replyDraft, setReplyDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function saveEdit() {
    if (!editDraft.trim()) return;
    await editComment.mutateAsync({ id: comment.id, content: editDraft });
    setEditing(false);
  }

  async function submitReply() {
    if (!replyDraft.trim()) return;
    await createComment.mutateAsync({ content: replyDraft, parentId: comment.id });
    setReplyDraft("");
    setReplying(false);
  }

  return (
    <div className={isReply ? "ml-8 mt-3" : "mt-4"}>
      <div
        className="rounded-lg border bg-card p-3"
        style={{ borderColor: "hsl(210,30%,88%)" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-foreground">
              {comment.authorName ?? "Unknown"}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatDateTime(comment.createdAt)}
            </span>
            {comment.editedAt && !comment.isDeleted && (
              <span className="text-xs text-muted-foreground/70">(edited)</span>
            )}
          </div>
          {isOwn && !comment.isDeleted && !editing && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => {
                  setEditDraft(comment.content);
                  setEditing(true);
                }}
                className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
                title="Edit"
              >
                <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-red-50 hover:text-red-600"
                title="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>

        <div className="mt-2">
          {comment.isDeleted ? (
            <p className="text-sm italic text-muted-foreground">[deleted]</p>
          ) : editing ? (
            <div className="space-y-2">
              <RichTextEditor
                content={editDraft}
                onChange={setEditDraft}
                toolbarVariant="minimal"
                minHeight={80}
              />
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={saveEdit} disabled={editComment.isPending}>
                  {editComment.isPending && (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" strokeWidth={1.5} />
                  )}
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div
              className="rich-text-content prose prose-sm max-w-none text-sm"
              dangerouslySetInnerHTML={{ __html: comment.content }}
            />
          )}
        </div>

        {/* Reply action — only on top-level comments (one nesting level). */}
        {!isReply && !comment.isDeleted && !editing && (
          <div className="mt-2">
            {replying ? (
              <div className="space-y-2">
                <RichTextEditor
                  content={replyDraft}
                  onChange={setReplyDraft}
                  toolbarVariant="minimal"
                  minHeight={70}
                  placeholder="Write a reply..."
                />
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={submitReply} disabled={createComment.isPending}>
                    {createComment.isPending && (
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" strokeWidth={1.5} />
                    )}
                    Reply
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setReplying(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setReplying(true)}
                className="text-xs font-medium text-primary hover:underline"
              >
                Reply
              </button>
            )}
          </div>
        )}
      </div>

      {/* Replies (one level) */}
      {comment.replies?.map((r) => (
        <CommentItem key={r.id} comment={r} issueId={issueId} isReply />
      ))}

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete comment?"
        description="This comment will be removed. If it has replies, it will show as [deleted] and the replies remain."
        onConfirm={async () => {
          await deleteComment.mutateAsync(comment.id);
        }}
      />
    </div>
  );
}

export default function IssueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: issue, isLoading, isError, error } = useIssue(id ?? "");
  const deleteIssue = useDeleteIssue();
  const createComment = useCreateComment(id ?? "");

  const [editOpen, setEditOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");

  // Support ?edit=true deep-link (used by the list row edit action pattern).
  useEffect(() => {
    if (searchParams.get("edit") === "true" && issue) {
      setEditOpen(true);
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue]);

  async function submitComment() {
    if (!commentDraft.trim()) return;
    await createComment.mutateAsync({ content: commentDraft });
    setCommentDraft("");
  }

  if (isLoading) {
    return (
      <AppLayout breadcrumb={[{ label: "QA" }, { label: "Issues" }]}>
        <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          Loading issue...
        </div>
      </AppLayout>
    );
  }

  if (isError || !issue) {
    return (
      <AppLayout breadcrumb={[{ label: "QA" }, { label: "Issues" }]}>
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
          <div className="font-medium">Issue not found</div>
          <div className="mt-1 font-mono text-xs opacity-80">
            {(error as Error)?.message ?? "This issue may not exist or you may not have access."}
          </div>
          <Button className="mt-4" variant="outline" onClick={() => navigate("/qa/issues")}>
            Back to Issues
          </Button>
        </div>
      </AppLayout>
    );
  }

  const topLevel = issue.comments ?? [];
  const totalComments = topLevel.reduce(
    (acc, c) => acc + 1 + (c.replies?.length ?? 0),
    0,
  );

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "QA" },
        { label: "Issues", href: "/qa/issues" },
        { label: issue.code },
      ]}
    >
      <div className="w-full space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => navigate("/qa/issues")}
              className="mt-1 flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
              title="Back"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <Bug className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <span className="font-mono text-xs text-muted-foreground">{issue.code}</span>
                <IssueStatusBadge status={issue.status} />
                <PriorityBadge priority={issue.priority} />
              </div>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight">{issue.title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
              title="Edit"
            >
              <Pencil className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => setDuplicateOpen(true)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
              title="Duplicate"
            >
              <Copy className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-red-600"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Main info */}
        <div
          className="rounded-xl border bg-card p-5"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
            <InfoField label="Product" value={issue.product?.name ?? "—"} />
            <InfoField label="Module" value={issue.module?.name ?? "—"} />
            <InfoField label="Submodule / Page" value={issue.submodule?.name ?? "—"} />
            <InfoField label="Category" value={humanize(issue.category)} />
            <InfoField label="Reporter" value={issue.reportedBy?.name ?? "—"} />
            <InfoField label="Assignee" value={issue.assignee?.name ?? "Unassigned"} />
            <InfoField label="Created" value={formatDateTime(issue.createdAt)} />
            <InfoField label="Updated" value={formatDateTime(issue.updatedAt)} />
          </div>

          <div className="mt-5">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Description
            </div>
            {issue.description ? (
              <div
                className="rich-text-content prose prose-sm mt-2 max-w-none"
                style={{ fontSize: 14, lineHeight: 1.8 }}
                dangerouslySetInnerHTML={{ __html: issue.description }}
              />
            ) : (
              <p className="mt-2 text-sm italic text-muted-foreground">No description.</p>
            )}
          </div>
        </div>

        {/* Attachments */}
        <div
          className="rounded-xl border bg-card p-5"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <div className="mb-3 flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <h2 className="text-sm font-semibold text-foreground">Attachments</h2>
          </div>
          <FileUploadZone
            entityType="issue"
            entityId={issue.id}
            entityLabel={issue.code}
            productId={issue.productId}
            showExisting
            hideVaultActions
          />
        </div>

        {/* Comments */}
        <div
          className="rounded-xl border bg-card p-5"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <div className="mb-3 flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <h2 className="text-sm font-semibold text-foreground">
              Comments {totalComments > 0 && `(${totalComments})`}
            </h2>
          </div>

          {/* New comment composer — sits at the top; newest comments first. */}
          <div className="mb-5 border-b pb-4" style={{ borderColor: "hsl(210,30%,88%)" }}>
            <RichTextEditor
              content={commentDraft}
              onChange={setCommentDraft}
              toolbarVariant="full"
              minHeight={90}
              placeholder="Add a comment..."
            />
            <div className="mt-2 flex justify-end">
              <Button
                onClick={submitComment}
                disabled={!commentDraft.trim() || createComment.isPending}
              >
                {createComment.isPending && (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
                )}
                Comment
              </Button>
            </div>
          </div>

          {topLevel.length === 0 ? (
            <p className="text-sm italic text-muted-foreground">
              No comments yet. Start the discussion above.
            </p>
          ) : (
            <div>
              {topLevel.map((c) => (
                <CommentItem key={c.id} comment={c} issueId={issue.id} />
              ))}
            </div>
          )}
        </div>
      </div>

      <NewIssueDrawer
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        editIssue={issue}
      />

      <NewIssueDrawer
        isOpen={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        duplicateFrom={issue}
        onSaved={() => navigate("/qa/issues")}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete issue?"
        description={`Are you sure you want to delete '${issue.title}'? It will be removed from the Issues list.`}
        onConfirm={async () => {
          await deleteIssue.mutateAsync(issue.id);
          navigate("/qa/issues");
        }}
      />
    </AppLayout>
  );
}
